import ks from "../ReUsableTS/kBrowserShared";
import { kStyles } from "../ReUsableTS/kStyles";
import LNG from "../ReUsableTS/kLNG";
import { kEditableTable, kEditableColumn, kEditableTableHeading, kHTMLEditableColType, kFilterUIController } from "../ReUsableTS/kEditableTable";
import { kColumnFilter } from "../ReUsableTS/kEditableTableFilter";
import { kColumnFilterPopup } from "../ReUsableTS/kEditableTableFilterPopup";
import { kAdvancedFilterDialog } from "../ReUsableTS/kEditableTableFilterDialog";
import { kFluentIcons } from "../ReUsableTS/kFluentIcons";

const FUNNEL_SVG: string = "<svg width=\"11\" height=\"11\" viewBox=\"0 0 16 16\" aria-hidden=\"true\">"
    + "<path d=\"M1 2h14l-5 6v5l-4 2V8z\" fill=\"currentColor\"/></svg>";

// One funnel affordance on one column header. Each is its own object holding its
// own column + grid, so its click handler reads `this` — no per-column closure
// or .bind (workspace OO rule). Opens the shared Tier-1 popup anchored to the
// funnel.
export class kFilterFunnel {
    public Grid: kEditableTable | null = null;
    public Column: kEditableColumn | null = null;
    public Controller: kEditableTableFilterUI | null = null;
    public Icon: HTMLSpanElement | null = null;

    public Build = (Grid: kEditableTable, Column: kEditableColumn, Heading: kEditableTableHeading,
        Controller: kEditableTableFilterUI): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Heading.HeaderCell === null) { return; }
            this.Grid = Grid;
            this.Column = Column;
            this.Controller = Controller;
            const Icon: HTMLSpanElement = document.createElement("span");
            Icon.className = "kFilterIcon";
            Icon.innerHTML = FUNNEL_SVG;
            Icon.title = LNG.FILTERTHISCOLUMN;
            Icon.setAttribute("role", "button");
            // aria-label (not just title, which some screen readers skip) and
            // aria-pressed (toggled active by kEditableTableHeading.SetFilterActive).
            Icon.setAttribute("aria-label", LNG.FILTERTHISCOLUMN);
            Icon.setAttribute("aria-pressed", "false");
            // Out of the TAB ORDER (2026-10-14): the grid's <table> is its one tab stop; the keyboard
            // opens this filter with Alt+ArrowDown on any cell of the column (kEditableTable._OpenActiveColumnFilter).
            Icon.setAttribute("tabindex", "-1");
            Icon.addEventListener("click", this.OnClick);
            Icon.addEventListener("keydown", this.OnKeyDown);
            Heading.HeaderCell.appendChild(Icon);
            Heading.FilterIcon = Icon;
            this.Icon = Icon;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFilterFunnel.Build");
        }
    };

    public OnClick = (Ev: Event): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            Ev.preventDefault();
            Ev.stopPropagation();
            if (this.Controller === null || this.Column === null || this.Icon === null) { return; }
            this.Controller.OpenColumnPopup(this.Column, this.Icon);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFilterFunnel.OnClick");
        }
    };

    public OnKeyDown = (Ev: KeyboardEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Ev.key === "Enter" || Ev.key === " ") { this.OnClick(Ev); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFilterFunnel.OnKeyDown");
        }
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// CHIP-BAR SOURCE
//
// The chip bar started life reading a kEditableTable directly. It is useful to
// anything that filters ANYTHING - a kanban board filtering task objects wants
// the same "here is what is filtering your view, click × to drop one" affordance
// as a grid. These two interfaces are all it actually needs, so a non-grid host
// can supply them. The grid keeps working through kGridFilterChipSource below,
// so no existing consumer changes.
// ─────────────────────────────────────────────────────────────────────────────

/** One active filter, as the bar needs to show it. Key identifies it for removal. */
export interface kFilterChipInfo {
    Key: string;
    Text: string;
}

/** What the chip bar reads from whatever owns the filters. */
export interface kFilterChipSource {
    ActiveFilters: () => kFilterChipInfo[];
    ClearFilter: (Key: string) => void;
    ClearAllFilters: () => void;
}

/** The grid's implementation - the behaviour the bar had hardcoded. */
export class kGridFilterChipSource implements kFilterChipSource {

    private Grid: kEditableTable;

    constructor(Grid: kEditableTable) {
        this.Grid = Grid;
    }

    public ActiveFilters = (): kFilterChipInfo[] => {
        if (ks.KErrorMode === true) { return []; }
        try {
            const Infos: kFilterChipInfo[] = [];
            let CurrFilter: kColumnFilter;
            for (CurrFilter of this.Grid.FilterModel.ColumnFilters) {
                Infos.push({ Key: CurrFilter.PropName, Text: CurrFilter.Describe() });
            }
            return Infos;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kGridFilterChipSource.ActiveFilters");
            return [];
        }
    };

    public ClearFilter = (Key: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Grid.ClearColumnFilter(Key);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kGridFilterChipSource.ClearFilter");
        }
    };

    public ClearAllFilters = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Grid.ClearFilters();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kGridFilterChipSource.ClearAllFilters");
        }
    };
}

// One removable chip describing one active filter. Its own object so the ×
// handler knows which filter to clear without a closure.
export class kFilterChip {
    public Source: kFilterChipSource | null = null;
    public PropName: string = "";
    public Element: HTMLSpanElement | null = null;
    /** Accessible name / tooltip on the × . Defaults to the grid wording. */
    public RemoveLabel: string = "";

    /** Grid-shaped overload kept so existing callers are untouched. */
    public Build = (Grid: kEditableTable, Filter: kColumnFilter): HTMLSpanElement => {
        if (ks.KErrorMode === true) { return document.createElement("span"); }
        try {
            return this.BuildFromInfo(new kGridFilterChipSource(Grid),
                { Key: Filter.PropName, Text: Filter.Describe() });
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFilterChip.Build");
            return document.createElement("span");
        }
    };

    public BuildFromInfo = (Source: kFilterChipSource, Info: kFilterChipInfo): HTMLSpanElement => {
        if (ks.KErrorMode === true) { return document.createElement("span"); }
        try {
            const Chip: HTMLSpanElement = document.createElement("span");
            this.Source = Source;
            this.PropName = Info.Key;
            if (this.RemoveLabel === "") { this.RemoveLabel = LNG.FILTERCLEARTHISCOLUMN; }
            Chip.className = "kFilterChip";
            const Label: HTMLSpanElement = document.createElement("span");
            Label.textContent = Info.Text;
            const Remove: HTMLSpanElement = document.createElement("span");
            Remove.className = "kFilterChipRemove";
            Remove.textContent = "×";
            Remove.setAttribute("role", "button");
            // Keyboard reachable + operable (mirrors kFilterFunnel): Tab to it,
            // Enter/Space to remove. title doubles as the accessible name.
            Remove.setAttribute("tabindex", "0");
            Remove.setAttribute("aria-label", this.RemoveLabel);
            Remove.title = this.RemoveLabel;
            Remove.addEventListener("click", this.OnRemove);
            Remove.addEventListener("keydown", this.OnKeyDown);
            Chip.appendChild(Label);
            Chip.appendChild(Remove);
            this.Element = Chip;
            return Chip;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFilterChip.BuildFromInfo");
            return document.createElement("span");
        }
    };

    public OnRemove = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Source === null) { return; }
            this.Source.ClearFilter(this.PropName);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFilterChip.OnRemove");
        }
    };

    public OnKeyDown = (Ev: KeyboardEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Ev.key === "Enter" || Ev.key === " ") { Ev.preventDefault(); this.OnRemove(); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFilterChip.OnKeyDown");
        }
    };
}

// The active-filter chip bar (feature #6). Renders one chip per active column
// condition plus a "Clear all" control, into a host element above the grid.
// Always visible state regardless of which surface set the filter.
export class kFilterChipBar {
    public Grid: kEditableTable | null = null;
    public Source: kFilterChipSource | null = null;
    public Host: HTMLDivElement | null = null;
    public Chips: kFilterChip[] = [];

    /** Labels, so a non-grid host is not stuck with "column" wording. Left empty
     *  they fall back to the grid's kLNG strings. */
    public RemoveLabel: string = "";
    public ClearAllLabel: string = "";

    /** Grid-shaped overload kept so existing callers are untouched. */
    public Build = (Grid: kEditableTable, Parent: HTMLElement): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Grid = Grid;
            this.BuildFromSource(new kGridFilterChipSource(Grid), Parent);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFilterChipBar.Build");
        }
    };

    public BuildFromSource = (Source: kFilterChipSource, Parent: HTMLElement): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            //A grid host gets the stylesheet from its own attach path; a standalone
            //host (a board, a card list) has no grid to install it, so do it here.
            //Guarded by element id inside AddStyles, so this stays idempotent.
            kEditableTableFilterUI.AddStyles();
            this.Source = Source;
            const Host: HTMLDivElement = document.createElement("div");
            Host.className = "kFilterChipBar";
            Host.style.display = "none";
            Parent.appendChild(Host);
            this.Host = Host;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFilterChipBar.BuildFromSource");
        }
    };

    public Refresh = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Source === null || this.Host === null) { return; }
            this.Host.innerHTML = "";
            this.Chips = [];
            const Infos: kFilterChipInfo[] = this.Source.ActiveFilters();
            if (Infos.length === 0) { this.Host.style.display = "none"; return; }
            // inline-flex (not flex): so the chip bar flows on the SAME row as the
            // filter input + Advanced button in a plain (non-flex) toolbar, instead of
            // wrapping to its own row. In a flex toolbar the value is blockified back to
            // flex, so flex-toolbar consumers are unaffected.
            this.Host.style.display = "inline-flex";
            let CurrInfo: kFilterChipInfo;
            for (CurrInfo of Infos) {
                const Chip: kFilterChip = new kFilterChip();
                Chip.RemoveLabel = this.RemoveLabel;
                this.Host.appendChild(Chip.BuildFromInfo(this.Source, CurrInfo));
                this.Chips.push(Chip);
            }
            this._AppendClearAll();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFilterChipBar.Refresh");
        }
    };

    private _AppendClearAll = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Host === null) { return; }
            const Clear: HTMLButtonElement = document.createElement("button");
            Clear.type = "button";
            Clear.className = "kFilterClearAll";
            Clear.textContent = LNG.FILTERCLEARALLFILTERS;
            if (this.ClearAllLabel !== "") { Clear.textContent = this.ClearAllLabel; }
            Clear.addEventListener("click", this.OnClearAll);
            this.Host.appendChild(Clear);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFilterChipBar._AppendClearAll");
        }
    };

    public OnClearAll = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Source === null) { return; }
            this.Source.ClearAllFilters();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kFilterChipBar.OnClearAll");
        }
    };
}

// The filter UI controller registered into the grid through
// kEditableTable.FilterUIFactory. Builds the header funnels + chip bar on
// attach, owns the single reused popup, and re-renders on Refresh (called by
// the grid after every model change). Importing this module is the whole
// opt-in (mirrors RowFormFactory).
export class kEditableTableFilterUI implements kFilterUIController {
    public Grid: kEditableTable | null = null;
    public Funnels: kFilterFunnel[] = [];
    public ChipBar: kFilterChipBar = new kFilterChipBar();
    public Popup: kColumnFilterPopup = new kColumnFilterPopup();
    public Advanced: kAdvancedFilterDialog = new kAdvancedFilterDialog();

    public static Create = (Grid: kEditableTable): kFilterUIController => {
        if (ks.KErrorMode === true) { return new kEditableTableFilterUI(); }
        try {
            const Controller: kEditableTableFilterUI = new kEditableTableFilterUI();
            Controller.Attach(Grid);
            return Controller;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilterUI.Create");
            return new kEditableTableFilterUI();
        }
    };

    public Attach = (Grid: kEditableTable): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kEditableTableFilterUI.AddStyles();
            this.Grid = Grid;
            this._BuildAdvancedButton();
            this._BuildChipBar();
            this._BuildFunnels();
            this.Refresh();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilterUI.Attach");
        }
    };

    private _BuildChipBar = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Grid === null) { return; }
            let Parent: HTMLElement | null = this.Grid.ToolbarDiv;
            if (Parent === null) { Parent = this.Grid.ContainerDiv; }
            if (Parent === null) { return; }
            this.ChipBar.Build(this.Grid, Parent);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilterUI._BuildChipBar");
        }
    };

    private _BuildFunnels = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Grid === null) { return; }
            let CurrHeading: kEditableTableHeading;
            for (CurrHeading of this.Grid.Headers) {
                const Column: kEditableColumn | null = this._ColumnFor(CurrHeading);
                if (Column === null) { continue; }
                if (kEditableTableFilterUI.IsFilterable(Column) === false) { continue; }
                const Funnel: kFilterFunnel = new kFilterFunnel();
                Funnel.Build(this.Grid, Column, CurrHeading, this);
                this.Funnels.push(Funnel);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilterUI._BuildFunnels");
        }
    };

    private _ColumnFor = (Heading: kEditableTableHeading): kEditableColumn | null => {
        if (ks.KErrorMode === true) { return null; }
        try {
            if (this.Grid === null) { return null; }
            if (Heading.ColumnIndex < 0 || Heading.ColumnIndex >= this.Grid.Columns.Items.length) { return null; }
            return this.Grid.Columns.Items[Heading.ColumnIndex];
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilterUI._ColumnFor");
            return null;
        }
    };

    public OpenColumnPopup = (Column: kEditableColumn, Anchor: HTMLElement): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Grid === null) { return; }
            this.Popup.Open(this.Grid, Column, Anchor);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilterUI.OpenColumnPopup");
        }
    };

    public OpenAdvanced = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Grid === null) { return; }
            this.Advanced.Open(this.Grid);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilterUI.OpenAdvanced");
        }
    };

    private _BuildAdvancedButton = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Grid === null || this.Grid.ToolbarDiv === null) { return; }
            const Button: HTMLButtonElement = document.createElement("button");
            Button.type = "button";
            Button.className = "kFilterAdvancedBtn";
            Button.textContent = LNG.KUI_OPEN_ADVANCED_FILTER;
            // The filter icon before the text (StorageCleanup review 2026-11-02: icons wherever possible).
            const Icon: HTMLSpanElement = document.createElement("span");
            Icon.setAttribute("aria-hidden", "true");
            Icon.style.cssText = "display:inline-flex;width:14px;height:14px;margin-inline-end:6px;vertical-align:-2px";
            Icon.innerHTML = kFluentIcons.Filter12().SVG.replace('width="20" height="20"', 'width="14" height="14"');
            Button.prepend(Icon);
            Button.addEventListener("click", this.OpenAdvanced);
            this.Grid.ToolbarDiv.appendChild(Button);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilterUI._BuildAdvancedButton");
        }
    };

    public Refresh = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.ChipBar.Refresh();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilterUI.Refresh");
        }
    };

    // Called from kEditableTable.Destroy. Closes the anchored popup (whose
    // window-level capture listeners would otherwise outlive the grid) and any
    // open advanced dialog.
    public Destroy = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Popup.Close();
            this.Advanced.Close();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilterUI.Destroy");
        }
    };

    // A column is filterable unless explicitly opted out or it is a pure action
    // (Button) column.
    public static IsFilterable = (Column: kEditableColumn): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Column.Filterable === false) { return false; }
            if (Column.Type === kHTMLEditableColType.Button) { return false; }
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilterUI.IsFilterable");
            return false;
        }
    };

    public static AddStyles = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (document.getElementById("kFilterUIStyles") !== null) { return; }
            const Css: string = kEditableTableFilterUI._Css();
            kStyles.AddStyles("kFilterUIStyles", Css);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilterUI.AddStyles");
        }
    };

    private static _Css = (): string => {
        if (ks.KErrorMode === true) { return ""; }
        // Funnel is hidden by default and only revealed on header hover/focus
        // (so it stays discoverable for setting a first filter); once a filter is
        // active on the column it is always shown in blue. Active wins over the
        // hover rule via !important so hovering the header never dims it.
        try {
        return `.kFilterIcon{display:inline-block;margin-left:5px;cursor:pointer;color:#605e5c;`
            + `vertical-align:middle;line-height:1;opacity:0;transition:opacity 0.12s ease;}`
            + `th:hover .kFilterIcon{opacity:0.55;}`
            + `.kFilterIcon:hover,.kFilterIcon:focus{opacity:0.9;}`
            + `.kFilterIcon.kFilterActive{opacity:1 !important;color:#0078d4;position:relative;}`
            // Non-colour cue (WCAG 1.4.1): a small dot badge appears when active,
            // so the state is not signalled by the blue tint alone.
            + `.kFilterIcon.kFilterActive::after{content:"";position:absolute;top:-2px;right:-3px;`
            + `width:5px;height:5px;border-radius:50%;background:#0078d4;border:1px solid #fff;}`
            // inline-flex so active-filter chips sit on the toolbar row (next to the filter
            // input + Advanced button), not on their own row below it. margin-left separates
            // them from the toolbar controls in a plain toolbar (no gap); vertical-align keeps
            // them centred against the input. In a flex toolbar the display is blockified to
            // flex and vertical-align is ignored, so those consumers render unchanged.
            + `.kFilterChipBar{display:inline-flex;flex-wrap:wrap;align-items:center;gap:6px;`
            + `padding:0 2px;margin-left:10px;vertical-align:middle;}`
            + `.kFilterChip{display:inline-flex;align-items:center;gap:6px;background:#eef3fb;`
            + `border:1px solid #c7dbf5;border-radius:14px;padding:3px 6px 3px 10px;font-size:12px;color:#1f1f1f;}`
            + `.kFilterChipRemove{cursor:pointer;font-weight:700;color:#605e5c;border-radius:50%;`
            + `width:16px;height:16px;display:inline-flex;align-items:center;justify-content:center;}`
            + `.kFilterChipRemove:hover{background:#d0e0f7;color:#1f1f1f;}`
            + `.kFilterClearAll{background:none;border:none;color:#0078d4;cursor:pointer;font-size:12px;`
            + `text-decoration:underline;padding:2px 4px;}`;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableFilterUI._Css");
            return "";
        }
    };
}

// NOTE: registration of this factory into kEditableTable.FilterUIFactory lives at
// the BOTTOM of kEditableTable.ts (not here). Doing it there — after the grid
// class is defined — lets the grid statically import this module so filtering is
// on by default with zero project code, while keeping this module free of any
// load-time reference to kEditableTable (which would deadlock the import cycle).
